import { cached, tracked } from '@glimmer/tracking';
import { type Registry as Services, service } from '@ember/service';
import { action } from '@ember/object';
import { dropTask, didCancel } from 'ember-concurrency';
import { waitFor } from '@ember/test-waiters';
import BaseController from 'uplisting-frontend/pods/base/controller';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import {
  BookingStatus,
  SafeguestVerificationStatus,
} from 'uplisting-frontend/models/booking';
import { SecurityDepositStatus } from 'uplisting-frontend/models/security-deposit';
import { BookingRentalAgreementStatus } from 'uplisting-frontend/models/booking-rental-agreement';
import {
  GuestIdentityVerificationStatus,
  OccasionType,
  TableSectionFields,
} from 'uplisting-frontend/models/occasion';
import {
  type OccasionFilterItemType,
  OccasionFilterType,
} from 'uplisting-frontend/models/advanced-report-configuration';
import { FilterId } from 'uplisting-frontend/utils/filters/abstract';
import {
  BookingWindow,
  type Filter,
  type ProtectionFilter,
  getFilterQueryParamName,
} from 'uplisting-frontend/utils/filters/types';
import { occasionState } from 'uplisting-frontend/utils/protection-status';
import { searchOptions } from 'uplisting-frontend/utils/search';
import type { IOccasionRequestMeta } from 'uplisting-frontend/utils/interfaces';

const ARRIVALS_WINDOW = 'next_30_days';
const PAST_WINDOW = 'past_90_days';
const SEARCH_FIELDS = ['guestName', 'propertyNickname'];
const PAGE_SIZE = 20;

export default class ProtectionOverviewController extends BaseController {
  @service('occasions') occasionsService!: Services['occasions'];

  @service('repositories/occasion')
  occasionRepositoryService!: Services['repositories/occasion'];

  @service('filters') filtersService!: Services['filters'];

  queryParams = [
    'propertyIds',
    'protectionStatuses',
    'page',
    { bookingWindow: 'window' },
  ];

  @tracked occasions: ModelRegistry['occasion'][] = [];
  @tracked propertyIds: string[] = [];
  @tracked protectionStatuses: string[] = [];
  @tracked bookingWindow: BookingWindow = BookingWindow.upcoming;
  @tracked searchQuery = '';
  @tracked page = 1;
  @tracked totalPages = 0;

  #filters?: ProtectionFilter[];
  #builtFrom?: string;

  // Rebuilt only when the query params change from outside this controller —
  // Back, Forward, or a pasted URL. Rebuilding on the host's own edit replaced
  // the controls mid-interaction, which shut an open dropdown and flashed the
  // list behind it.
  get filterList(): ProtectionFilter[] {
    const signature = this.#queryParamSignature;

    if (!this.#filters || this.#builtFrom !== signature) {
      this.#filters = [
        this.filtersService.createFilterFor(
          FilterId.bookingWindow,
          this.bookingWindow,
        ) as ProtectionFilter,
        this.filtersService.createFilterFor(
          FilterId.property,
          this.propertyIds,
        ) as ProtectionFilter,
        this.filtersService.createFilterFor(
          FilterId.protectionStatus,
          this.protectionStatuses,
        ) as ProtectionFilter,
      ];

      this.#builtFrom = signature;
    }

    return this.#filters;
  }

  public resetFilters(): void {
    this.#filters = undefined;
    this.#builtFrom = undefined;
  }

  get #queryParamSignature(): string {
    return JSON.stringify([
      this.bookingWindow,
      [...this.propertyIds].sort(),
      [...this.protectionStatuses].sort(),
    ]);
  }

  // The window always carries a value, so only a move away from the default
  // counts as the host narrowing the list.
  @cached
  get isFiltered(): boolean {
    return (
      this.searchQuery !== '' ||
      this.propertyIds.length > 0 ||
      this.protectionStatuses.length > 0 ||
      this.bookingWindow !== BookingWindow.upcoming
    );
  }

  @cached
  get isPast(): boolean {
    return this.bookingWindow === BookingWindow.past;
  }

  @cached
  get visibleOccasions(): ModelRegistry['occasion'][] {
    const { protectionStatuses } = this;

    const byStatus =
      protectionStatuses.length === 0
        ? this.occasions
        : this.occasions.filter((occasion) =>
            protectionStatuses.includes(occasionState(occasion)),
          );

    return searchOptions(byStatus, this.searchQuery, SEARCH_FIELDS);
  }

  @cached
  get dateWindowFilter(): OccasionFilterItemType {
    if (this.isPast) {
      return {
        field: TableSectionFields.checkOut,
        type: OccasionFilterType.between,
        values: [PAST_WINDOW],
      };
    }

    return {
      type: OccasionFilterType.or,
      values: [
        {
          field: TableSectionFields.stays,
          type: OccasionFilterType.between,
          values: ['today'],
        },
        {
          field: TableSectionFields.checkIn,
          type: OccasionFilterType.between,
          values: [ARRIVALS_WINDOW],
        },
        {
          field: TableSectionFields.checkOut,
          type: OccasionFilterType.between,
          values: ['today'],
        },
      ],
    };
  }

  @cached
  get protectedItemFilter(): OccasionFilterItemType {
    return {
      type: OccasionFilterType.or,
      values: [
        {
          field: TableSectionFields.depositStatus,
          type: OccasionFilterType.in,
          values: Object.values(SecurityDepositStatus),
        },
        {
          field: TableSectionFields.bookingGuestIdentityVerificationStatus,
          type: OccasionFilterType.in,
          values: Object.values(GuestIdentityVerificationStatus),
        },
        {
          field: TableSectionFields.bookingRentalAgreementStatus,
          type: OccasionFilterType.in,
          values: Object.values(BookingRentalAgreementStatus),
        },
        {
          field: TableSectionFields.safeguestStatus,
          type: OccasionFilterType.in,
          values: Object.values(SafeguestVerificationStatus),
        },
      ],
    };
  }

  @cached
  get propertyFilter(): OccasionFilterItemType | undefined {
    if (this.propertyIds.length === 0) {
      return undefined;
    }

    return {
      field: TableSectionFields.propertyId,
      type: OccasionFilterType.in,
      values: this.propertyIds,
    };
  }

  @cached
  get filters(): OccasionFilterItemType[] {
    return [
      {
        field: TableSectionFields.bookingStatus,
        type: OccasionFilterType.notIn,
        values: [BookingStatus.cancelled],
      },
      this.dateWindowFilter,
      this.protectedItemFilter,
      this.propertyFilter,
    ].filter(Boolean) as OccasionFilterItemType[];
  }

  @action
  handleOpenBooking(bookingId: string): void {
    this.router.transitionTo('protection.overview.bookings.summary', bookingId);
  }

  @action
  handleSearchInput(event: Event): void {
    this.searchQuery = (event.target as HTMLInputElement).value;
  }

  @action
  @waitFor
  public async handleFilterUpdate(filter: Filter): Promise<void> {
    const queryParam = getFilterQueryParamName(filter);

    this[queryParam] = filter.toQuery();

    this.#builtFrom = this.#queryParamSignature;

    if (filter.id !== FilterId.protectionStatus) {
      this.page = 1;

      await this.fetchData();
    }
  }

  @action
  @waitFor
  public async handlePageChange(page: number): Promise<void> {
    if (this.fetchDataTask.isRunning) {
      return;
    }

    this.page = page;

    await this.fetchData();
  }

  @action
  @waitFor
  public async fetchData(): Promise<void> {
    try {
      await this.fetchDataTask.perform();
    } catch (e) {
      if (!didCancel(e)) {
        throw e;
      }
    }
  }

  public fetchDataTask = dropTask(async () => {
    let data = await this.queryOccasions();

    const { meta } = data as unknown as IOccasionRequestMeta;

    this.totalPages = meta?.total_pages ?? 0;

    if (this.totalPages > 0 && this.page > this.totalPages) {
      this.page = this.totalPages;

      data = await this.queryOccasions();
    }

    this.occasions.forEach((occasion) => {
      if (data.includes(occasion) || occasion.isDestroyed) {
        return;
      }

      occasion.unloadRecord();
    });

    this.occasions = data.slice();
  });

  private async queryOccasions() {
    return this.occasionRepositoryService.query({
      filter: {
        occasion_type: OccasionType.booking,
        occasion_filters: this.filters,
      },
      fields: {
        occasions: [
          this.occasionRepositoryService.bookingFields,
          TableSectionFields.safeguestStatus,
          'booking_safeguest_amount',
          'booking_security_deposit_claimed_amount',
          'booking_security_deposit_released_at',
          'booking_security_deposit_expires_at',
        ].join(','),
      },
      sort: this.isPast
        ? `-${TableSectionFields.checkOut}`
        : TableSectionFields.checkIn,
      time_zone: this.defaultTimezone,
      page: {
        number: this.page,
        size: PAGE_SIZE,
      },
    });
  }
}
